import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from '~spa/Components/Link/Link';
import { navigatePage } from '~spa/Utils/WrapperIntegration/Ucf';
import { navigateBack } from '~shared/utils/win';
import HorizontalScrollWithMask from '~shared/components/horizontal-scroll-with-mask';
import {
    WRAPPER_CSS_ID,
    BREADCRUMBS_LEVELONE_CLASS_NAME,
    BREADCRUMBS_RETURNLINK_CLASS_NAME,
    BREADCRUMBS_LEVELTWO_TEXT,
    BREADCRUMBS_LEVELTWO_CLASS_NAME,
    BREADCRUMBS_INVISIBLE_CLASS_NAME,
    BREADCRUMBS_LAST_VISIBLE_CLASS_NAME,
    BREADCRUMBS_CSS_CONTAINER,
    BREADCRUMBS_BACK_BUTTON_CLASS_NAME,
} from './constants';
import {
    generateBreadcrumbSpectateProduct,
 } from './utils';
import './styles/breadcrumbs.scss';
import { rewriteUrl } from '~shared/utils/uriAliases';

/**
 * Breadcrumbs rendering. Implement google's breadcrumb data-type using Microdata format.
 *
 * @param {Object} props The props
 * @returns {Object} The Breacrumbs element
 *
 * @see https://developers.google.com/search/docs/data-types/breadcrumb
 */
const Breadcrumbs = ({data}) => {
    const [ t ] = useTranslation();

    const breadcrumbsElements = data.map(({label, url = null, invisible = false, lastVisible = false}, position) => {
        const positionOffset = 2; // itemProp position starts from 1 which is the home link, so the dynamic elements need this offset.
        const breadcrumbLevel = position + positionOffset;

        // We dont want to show it on the current breadcrumb level as you cant click last level
        const dataSpectateProduct = lastVisible ? null : generateBreadcrumbSpectateProduct(data, breadcrumbLevel);

        const className = classNames(
            { [BREADCRUMBS_INVISIBLE_CLASS_NAME] : invisible },
            { [BREADCRUMBS_LEVELONE_CLASS_NAME] : !invisible && !position },
            { [BREADCRUMBS_LEVELTWO_CLASS_NAME] : !invisible && position },
            { [BREADCRUMBS_LAST_VISIBLE_CLASS_NAME]: !invisible && lastVisible },
        );

        return (
            <span key={rewriteUrl(url)} className={className} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link
                    to={url}
                    itemProp="item"
                >
                    { invisible
                        ? <meta className={BREADCRUMBS_LEVELTWO_TEXT} itemProp="name" content={label} />
                        : <span className={BREADCRUMBS_LEVELTWO_TEXT} data-spectate-product={dataSpectateProduct} itemProp="name" dir="auto">{label}</span>
                    }
                    <meta itemProp="position" content={breadcrumbLevel} />
                </Link>
            </span>
        );
    });

    const handleGoBack = () => {
        if (window.history.state) {
            navigateBack();
        } else {
            const [breadcrumbOneLevelUp] = data.slice(-2, -1);
            const breadcrumbOneLevelUpUrl = breadcrumbOneLevelUp?.url || '/';
            navigatePage(breadcrumbOneLevelUpUrl);
        }
    };

    return (
        <div className={BREADCRUMBS_CSS_CONTAINER}>
            <div
                className={BREADCRUMBS_BACK_BUTTON_CLASS_NAME}
                data-spectate-product={generateBreadcrumbSpectateProduct(data)}
                onClick={handleGoBack}
                role="button"
            ></div>
            <div className={WRAPPER_CSS_ID} itemScope itemType="https://schema.org/BreadcrumbList">
                <HorizontalScrollWithMask>
                    <span className={BREADCRUMBS_RETURNLINK_CLASS_NAME} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <Link to="/" itemProp="item">
                            <span data-spectate-product={generateBreadcrumbSpectateProduct(data, 1)}>{t('breadcrumbs.home')}</span>
                            <meta itemProp="name" content={t('breadcrumbs.home_meta')} />
                            <meta itemProp="position" content="1" />
                        </Link>
                    </span>
                    {breadcrumbsElements}
                </HorizontalScrollWithMask>
            </div>
        </div>
    );
};

Breadcrumbs.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        url: PropTypes.string,
    })),
};

export default Breadcrumbs;
